// src/pages/CalendarPage.tsx
// Submit Time Sheet – Calendar monthly view
// Displays a month grid with time tracking events from TaskTimeTracking/GetEvents

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';
import type { CalendarEvent, GetCalendarEventsRequest } from '../types/calendarTypes';
import { SubmitTime } from '../components/SubmitTime/SubmitTime';
import type { TimeEntryEdit } from '../components/SubmitTime/types';
import './CalendarPage.css';

const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Parse "H:MM" or "HH:MM" to total minutes */
function parseHoursToMinutes(hours: string): number
{
  if (!hours) return 0;
  const [h, m] = hours.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Total minutes back to "H:MM" */
function minutesToHoursStr(totalMinutes: number): string
{
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** Parse the API date string "M/D/YYYY h:mm:ss AM/PM" into a Date key "YYYY-M-D" */
function parseDateKey(dateStr: string): string
{
  if (!dateStr) return '';
  // Handle both "1/2/2026 6:00:00 AM" format and ISO format
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function dateKey(date: Date): string
{
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/** Get the first Sunday on or before the 1st of the month */
function getCalendarStart(year: number, month: number): Date
{
  const first = new Date(year, month, 1);
  const day = first.getDay(); // 0=Sun
  return new Date(year, month, 1 - day);
}

/** Get the last Saturday on or after the last day of the month */
function getCalendarEnd(year: number, month: number): Date
{
  const last = new Date(year, month + 1, 0);
  const day = last.getDay(); // 0=Sun..6=Sat
  return new Date(year, month + 1, 0 + (6 - day));
}

export const CalendarPage: React.FC = () =>
{
  const today = useMemo(() => new Date(), []);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSubmitTime, setShowSubmitTime] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const isPluginBeta = localStorage.getItem('C2_isSecurityGroupBeta') === 'true';

  // ---- Navigation ----
  const goToPrevMonth = useCallback(() =>
  {
    setCurrentMonth(m =>
    {
      if (m === 0)
      {
        setCurrentYear(y => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goToNextMonth = useCallback(() =>
  {
    setCurrentMonth(m =>
    {
      if (m === 11)
      {
        setCurrentYear(y => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const goToToday = useCallback(() =>
  {
    const now = new Date();
    setCurrentYear(now.getFullYear());
    setCurrentMonth(now.getMonth());
  }, []);

  // ---- Data fetching ----
  const fetchEvents = useCallback(async (year: number, month: number) =>
  {
    setLoading(true);
    try
    {
      const start = getCalendarStart(year, month);
      const end = getCalendarEnd(year, month);

      // End should be end-of-day in UTC
      const req: GetCalendarEventsRequest = {
        StartDate: new Date(
          Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 6, 0, 0, 0),
        ).toISOString(),
        EndDate: new Date(
          Date.UTC(end.getFullYear(), end.getMonth(), end.getDate() + 1, 5, 59, 59, 999),
        ).toISOString(),
      };

      const data = await apiService.post<CalendarEvent[]>('TaskTimeTracking/GetEvents', req);
      setEvents(Array.isArray(data) ? data : []);
    }
    catch (err)
    {
      console.error('Error fetching calendar events:', err);
      setEvents([]);
    }
    finally
    {
      setLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    fetchEvents(currentYear, currentMonth);
  }, [currentYear, currentMonth, fetchEvents]);

  // ---- Group events by date key ----
  const eventsByDate = useMemo(() =>
  {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events)
    {
      const key = parseDateKey(ev.Date);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return map;
  }, [events]);

  // ---- Merge events sharing the same ReplyId within each day, summing their hours ----
  const mergedEventsByDate = useMemo(() =>
  {
    const result = new Map<string, CalendarEvent[]>();
    for (const [key, dayEvs] of eventsByDate)
    {
      // Bucket by ReplyId; entries with ReplyId === 0 stay individual (keyed by -TimeTrackingID)
      const byReplyId = new Map<number, CalendarEvent[]>();
      for (const ev of dayEvs)
      {
        const rid = ev.ReplyId || 0;
        if (rid === 0)
        {
          byReplyId.set(-ev.TimeTrackingID, [ev]);
        }
        else
        {
          if (!byReplyId.has(rid)) byReplyId.set(rid, []);
          byReplyId.get(rid)!.push(ev);
        }
      }
      const merged: CalendarEvent[] = [];
      for (const group of byReplyId.values())
      {
        if (group.length === 1)
        {
          merged.push(group[0]!);
        }
        else
        {
          const totalMinutes = group.reduce((sum, ev) => sum + parseHoursToMinutes(ev.Hours), 0);
          merged.push({ ...group[0]!, Hours: minutesToHoursStr(totalMinutes) });
        }
      }
      result.set(key, merged);
    }
    return result;
  }, [eventsByDate]);

  // ---- Build calendar grid cells ----
  const calendarCells = useMemo(() =>
  {
    const cells: { date: Date; isOtherMonth: boolean }[] = [];
    const start = getCalendarStart(currentYear, currentMonth);
    const end = getCalendarEnd(currentYear, currentMonth);

    const d = new Date(start);
    while (d <= end)
    {
      cells.push({
        date: new Date(d),
        isOtherMonth: d.getMonth() !== currentMonth,
      });
      d.setDate(d.getDate() + 1);
    }
    return cells;
  }, [currentYear, currentMonth]);

  // ---- Edit mode ----
  const handleEventClick = useCallback((ev: CalendarEvent) =>
  {
    setSelectedEvent(ev);
    setShowSubmitTime(true);
  }, []);

  const handleSubmitTimeClose = useCallback(() =>
  {
    setShowSubmitTime(false);
    setSelectedEvent(null);
  }, []);

  // Map CalendarEvent → TimeEntryEdit shape expected by SubmitTime objedit
  const editObjedit = useMemo((): TimeEntryEdit | null =>
  {
    if (!selectedEvent) return null;
    const d = new Date(selectedEvent.Date);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      TimeTrackingID: selectedEvent.TimeTrackingID,
      LayerId: selectedEvent.LayerId,
      ProjectID: selectedEvent.ProjectID,
      ProjectName: selectedEvent.CoreTask?.ProjectName || '',
      ConfigValueID: selectedEvent.CoreTask?.ConfigValueID ?? 0,
      SideMenuText: selectedEvent.CoreTask?.SideMenuText || '',
      ConversationId: selectedEvent.ConversationId,
      CoreTaskID: selectedEvent.CoreTaskID,
      title: selectedEvent.CoreTask?.Name || '',
      Hours: selectedEvent.Hours,
      date: dateStr,
      Description: selectedEvent.Description || '',
      attachments: [],
      TeamId: selectedEvent.TeamId || undefined,
      replyId: selectedEvent.ReplyId || 0,
    };
  }, [selectedEvent]);

  const todayKey = dateKey(today);

  return (
    <div className="calendar-page">
      {/* ---- Header ---- */}
      <div className="calendar-header">
        <div className="calendar-header-left">
          <h1 className="calendar-title">Submit Time Sheet</h1>
          <div className="calendar-nav">
            <button className="calendar-nav-btn" onClick={goToPrevMonth} title="Previous month">
              <i className="fa fa-caret-left" />
            </button>
            <span className="calendar-month-label">
              {MONTH_NAMES[currentMonth]} {currentYear}
            </span>
            <button className="calendar-nav-btn" onClick={goToNextMonth} title="Next month">
              <i className="fa fa-caret-right" />
            </button>
          </div>
        </div>

        <div className="calendar-header-right">
          <button
            className={`calendar-btn ${isPluginBeta ? 'enable-help-tooltip disabled' : ''}`}
            onClick={() => setShowSubmitTime(true)}
            disabled={isPluginBeta}
            data-title={isPluginBeta ? 'Perform your records from your tasks' : 'Submit Time'}
          >
            Submit time
          </button>
          <button className="calendar-btn" onClick={goToToday}>
            Today
          </button>
        </div>
      </div>

      {/* ---- Calendar Grid ---- */}
      {loading ? (
        <div className="calendar-loading">
          <div className="calendar-loading-spinner" />
          Loading events…
        </div>
      ) : (
        <div className="calendar-grid-wrapper">
          <div className="calendar-grid">
            {/* Day headers */}
            {DAY_NAMES.map(name => (
              <div key={name} className="calendar-day-header">
                {name}
              </div>
            ))}

            {/* Day cells */}
            {calendarCells.map(({ date, isOtherMonth }) =>
            {
              const key = dateKey(date);
              const isToday = key === todayKey;
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const dayEvents = mergedEventsByDate.get(key) || [];

              return (
                <div
                  key={key}
                  className={[
                    'calendar-day-cell',
                    isToday && 'is-today',
                    isOtherMonth && 'is-other-month',
                    isWeekend && 'is-weekend',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="calendar-day-number">{date.getDate()}</div>
                  <div className="calendar-events">
                    {dayEvents.map(ev => (
                      <div
                        key={ev.TimeTrackingID}
                        className="calendar-event-item"
                        title={`${ev.CoreTask?.Name || 'Task'} — ${ev.Hours}`}
                        onClick={() => handleEventClick(ev)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="calendar-event-icon">
                          <i className={ev.CoreTask?.Icon || 'icons-menu-tasks'} />
                        </span>
                        <span className="calendar-event-name">{ev.CoreTask?.Name || 'Task'}</span>
                        <span className="calendar-event-hours">({ev.Hours})</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SubmitTime standalone modal (type 2) — new entry or edit on event click */}
      {showSubmitTime && (
        <SubmitTime
          openmodal={showSubmitTime}
          typesubmittime={2}
          api={apiService}
          isedit={!!selectedEvent}
          objedit={editObjedit as any}
          onclose={handleSubmitTimeClose}
          onClose={handleSubmitTimeClose}
          onSubmitTime={() => fetchEvents(currentYear, currentMonth)}
        />
      )}
    </div>
  );
};

export default CalendarPage;
